import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Container } from '../../styles/components.js';
import { AppImages } from '../../assets/index.js';

/* ─── Styled Components ──────────────────────────────────────────── */
const SkillsSection = styled.section`
  padding: ${({ theme }) => theme.spacing.xxl} 0;
  background-color: ${({ theme }) => theme.colors.background};
  position: relative;
  overflow: hidden;
`;

const SectionHeader = styled.div`
  text-align: center;
  max-width: 650px;
  margin: 0 auto ${({ theme }) => theme.spacing.xl};
`;

const Label = styled.div`
  display: inline-block;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primary}0a;
  padding: 5px 14px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  border: 1px solid ${({ theme }) => theme.colors.primary}18;
  margin-bottom: 1rem;
`;

const Title = styled.h2`
  font-size: clamp(1.8rem, 3.5vw, 2.6rem);
  font-weight: 800;
  color: ${({ theme }) => theme.colors.textDark};
  margin-bottom: 1rem;
  letter-spacing: -0.03em;
  span {
    background: ${({ theme }) => theme.colors.gradient};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const Desc = styled.p`
  font-size: 1.05rem;
  color: ${({ theme }) => theme.colors.textGray};
  line-height: 1.7;
  margin-bottom: 0;
`;

/* ─── BENTO GRID ARCHITECTURE ─────────────────────────────────── */
const BentoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: ${({ theme }) => theme.breakpoints.laptop}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const BentoCard = styled(motion.div)`
  background: ${({ theme }) => theme.colors.surfaceAlt};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 24px;
  padding: 2.25rem;
  box-shadow: ${({ theme }) => theme.shadows.card};
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  &.experience-card {
    grid-column: span 2;
    @media (max-width: ${({ theme }) => theme.breakpoints.laptop}) {
      grid-column: span 2;
    }
    @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
      grid-column: span 1;
    }
  }

  &.proficiency-card {
    grid-column: span 1;
  }

  &.tech-card {
    grid-column: span 3;
    @media (max-width: ${({ theme }) => theme.breakpoints.laptop}) {
      grid-column: span 2;
    }
    @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
      grid-column: span 1;
    }
  }
`;

const BentoTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.textDark};
  margin-bottom: 1.75rem;
  letter-spacing: -0.02em;
  display: flex;
  align-items: center;
  gap: 10px;
`;

/* ─── TIMELINE ─────────────────────────────────────────────── */
const Timeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 8px;
    bottom: 8px;
    left: 8px;
    width: 1px;
    background-color: ${({ theme }) => theme.colors.border};
  }
`;

const TimelineNode = styled(motion.div)`
  display: flex;
  gap: 1.5rem;
  position: relative;
`;

const TimelineMarker = styled.div`
  width: 17px;
  height: 17px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.surface};
  border: 3px solid ${({ theme }) => theme.colors.primary};
  z-index: 2;
  margin-top: 5px;
  flex-shrink: 0;
  box-shadow: ${({ theme }) => theme.shadows.glowSm};
`;

const TimelineDetails = styled.div`
  flex: 1;
`;

const Role = styled.h4`
  font-size: 1.05rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.textDark};
  margin-bottom: 4px;
  letter-spacing: -0.02em;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
`;

const Period = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primary}0c;
  border: 1px solid ${({ theme }) => theme.colors.primary}15;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  padding: 2px 10px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const TimelineText = styled.p`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textGray};
  line-height: 1.65;
  margin-bottom: 0;
`;

/* ─── PROFICIENCY ────────────────────────────────────────────── */
const SkillSlider = styled(motion.div)`
  margin-bottom: 1.25rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SliderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const SkillName = styled.span`
  font-weight: 600;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.textDark};
`;

const SkillValue = styled.span`
  font-size: 0.8rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  font-family: 'Space Grotesk', sans-serif;
`;

const BarOuter = styled.div`
  height: 6px;
  background-color: ${({ theme }) => theme.colors.surface};
  border-radius: 99px;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const BarInner = styled(motion.div)`
  height: 100%;
  background: ${({ theme }) => theme.colors.gradient};
  border-radius: 99px;
`;

/* ─── TECH STACK GRID ────────────────────────────────────────── */
const TechGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const TechBadge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  font-size: 0.85rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textDark};
  cursor: default;
  transition: all 0.25s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary}50;
    background: ${({ theme }) => theme.colors.primary}0c;
    color: ${({ theme }) => theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.glowSm};
  }

  span.icon {
    font-size: 1.15rem;
    display: flex;
    align-items: center;
    justify-content: center;
    
    img {
      width: 1.25rem;
      height: 1.25rem;
      object-fit: contain;
    }
  }
`;

/* ─── Data ────────────────────────────────────────────────────── */
const experience = [
  {
    role: 'Senior React Native Developer',
    period: '2023 – Present',
    desc: 'Leading development of complex mobile applications, architecting scalable solutions, and mentoring junior developers. Focused on performance optimization and clean code practices.'
  },
  {
    role: 'React Native Developer',
    period: '2022 – 2023',
    desc: 'Developed and maintained cross-platform mobile applications with React Native, Redux, and TypeScript. Integrated RESTful APIs and implemented complex UI components.'
  },
  {
    role: 'Frontend Developer',
    period: '2021 – 2021',
    desc: 'Built responsive web applications using React.js and modern JavaScript. Collaborated with designers to implement pixel-perfect UIs and ensure great user experience.'
  },
];

const skills = [
  { name: 'React Native', percent: 95 },
  { name: 'JavaScript (ES6+)', percent: 92 },
  { name: 'React.js', percent: 90 },
  { name: 'Redux / State Management', percent: 88 },
  { name: 'API & Backend Integration', percent: 88 },
  { name: 'TypeScript', percent: 85 },
  { name: 'Stripe Payment Gateway', percent: 82 },
  { name: 'In-App Purchases', percent: 80 },
];

const techStack = [
  { icon: '⚛️', name: 'React Native' },
  { icon: <img src={AppImages.typeScript} alt="TypeScript" />, name: 'TypeScript' },
  { icon: '🔵', name: 'Redux' },
  { icon: '🔥', name: 'Firebase' },
  { icon: '💳', name: 'Stripe' },
  { icon: '🗺️', name: 'Maps SDK' },
  { icon: '🔔', name: 'Push Notifications' },
  { icon: '🛒', name: 'In-App Purchases' },
  { icon: '📊', name: 'Amplitude' },
  { icon: <img src={AppImages.githubLogo} alt="GitHub" />, name: 'Git & GitHub' },
  { icon: <img src={AppImages.mongodb} alt="MongoDB" />, name: 'MongoDB' },
  { icon: <img src={AppImages.express} alt="Express" />, name: 'Express' },
  { icon: <img src={AppImages.androidStudio} alt="Android Studio" />, name: 'Android Studio' },
  { icon: <img src={AppImages.xcode} alt="Xcode" />, name: 'Xcode' },
  { icon: '🔑', name: 'Social Auth' },
  { icon: '📱', name: 'AdMob' },
];

const Skills = () => {
  return (
    <SkillsSection id="skills">
      <Container>
        <SectionHeader>
          <Label>02 / EXPERTISE</Label>
          <Title>Technical <span>Skills & Experience</span></Title>
          <Desc>
            With 3+ years of focused experience in React Native, I deliver robust, scalable, and maintainable mobile solutions.
          </Desc>
        </SectionHeader>

        <BentoGrid>
          {/* Experience Cell */}
          <BentoCard
            className="experience-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            <div>
              <BentoTitle>🗓 Work History</BentoTitle>
              <Timeline>
                {experience.map((item, i) => (
                  <TimelineNode
                    key={i}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1, duration: 0.5 }}
                  >
                    <TimelineMarker />
                    <TimelineDetails>
                      <Role>{item.role}</Role>
                      <MetaRow>
                        <Period>{item.period}</Period>
                      </MetaRow>
                      <TimelineText>{item.desc}</TimelineText>
                    </TimelineDetails>
                  </TimelineNode>
                ))}
              </Timeline>
            </div>
          </BentoCard>

          {/* Proficiency Cell */}
          <BentoCard
            className="proficiency-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          >
            <div>
              <BentoTitle>📊 Key Strengths</BentoTitle>
              {skills.map((skill, i) => (
                <SkillSlider key={skill.name}>
                  <SliderHeader>
                    <SkillName>{skill.name}</SkillName>
                    <SkillValue>{skill.percent}%</SkillValue>
                  </SliderHeader>
                  <BarOuter>
                    <BarInner
                      initial={{ width: 0 }}
                      whileInView={{ width: `${skill.percent}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 1, delay: 0.2 + i * 0.05 }}
                    />
                  </BarOuter>
                </SkillSlider>
              ))}
            </div>
          </BentoCard>

          {/* Tech Stack Cell */}
          <BentoCard
            className="tech-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          >
            <div>
              <BentoTitle>🛠 Tech Stack & Tooling</BentoTitle>
              <TechGrid>
                {techStack.map((tech, i) => (
                  <TechBadge
                    key={tech.name}
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.03 }}
                  >
                    <span className="icon">{tech.icon}</span>
                    {tech.name}
                  </TechBadge>
                ))}
              </TechGrid>
            </div>
          </BentoCard>
        </BentoGrid>
      </Container>
    </SkillsSection>
  );
};

export default Skills;